/* Legal pages — Privacy / Refund / Terms / Contact.
 *
 * One file, four exported page components sharing the same `LegalShell`
 * wrapper so the brand chrome + back link + footer stay consistent. Copy
 * is tailored to Mera Naksha: concept-plan-only positioning, Indian
 * jurisdiction, and the central WhatsApp number for support.
 */
import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ShieldCheck, FileText, RefreshCcw, ScrollText, Phone, Mail, MapPin } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import BrandLogo from "@/components/BrandLogo";

const LegalShell = ({ title, Icon, kicker, lastUpdated, children, testId }) => {
  useEffect(() => {
    document.title = `${title} · Mera Naksha`;
  }, [title]);
  return (
    <div className="min-h-screen bg-sand" data-testid={testId}>
      <header className="bg-navy text-white border-b-4 border-gold">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center bg-white rounded-lg px-2 py-1 shadow-sm">
            <BrandLogo size="sm" asLink={false} withTagline lang="en" testId="legal-brand-logo" />
          </Link>
          <Link to="/" data-testid="legal-back" className="inline-flex items-center gap-1.5 text-[12.5px] font-bold text-white/85 hover:text-gold">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-10">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gold/15 border border-gold/30 text-gold-dark text-[10.5px] font-bold tracking-widest uppercase">
          <Icon className="h-3.5 w-3.5" /> {kicker}
        </div>
        <h1 className="mt-2 text-2xl sm:text-4xl font-extrabold tracking-tight text-navy">{title}</h1>
        <div className="mt-1 text-[12px] text-muted-foreground">Last updated: {lastUpdated}</div>
        <article className="prose prose-sm sm:prose-base mt-6 max-w-none prose-headings:text-navy prose-headings:font-extrabold prose-h2:text-lg prose-h2:mt-7 prose-p:leading-relaxed prose-li:leading-relaxed prose-strong:text-navy">
          {children}
        </article>
        <Footer />
      </main>
    </div>
  );
};

const Footer = () => (
  <div className="mt-10 rounded-2xl bg-white border-2 border-border p-4 text-[13px]">
    <div className="font-extrabold text-navy">Contact Team Mera Naksha</div>
    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-muted-foreground">
      <a href="https://wa.me/919717667115" target="_blank" rel="noopener noreferrer" data-testid="legal-contact-wa" className="inline-flex items-center gap-1.5 text-navy font-bold hover:text-gold-dark">
        <FaWhatsapp className="h-3.5 w-3.5 text-emerald-600" /> WhatsApp +91 97176 67115
      </a>
      <a href="mailto:support@meranaksha.com" data-testid="legal-contact-email" className="inline-flex items-center gap-1.5 text-navy font-bold hover:text-gold-dark">
        <Mail className="h-3.5 w-3.5" /> support@meranaksha.com
      </a>
    </div>
    <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11.5px] font-bold">
      <Link to="/privacy" className="text-navy hover:text-gold-dark">Privacy</Link>
      <Link to="/refund" className="text-navy hover:text-gold-dark">Refund Policy</Link>
      <Link to="/terms" className="text-navy hover:text-gold-dark">Terms</Link>
      <Link to="/contact" className="text-navy hover:text-gold-dark">Contact</Link>
    </div>
    {/* Government registration credentials — mandatory display now that
        Mera Naksha is a govt-registered entity. Muted styling to keep the
        primary contact CTAs visually dominant. */}
    <div
      className="mt-3 pt-3 border-t border-border text-[11px] text-muted-foreground leading-relaxed"
      data-testid="legal-footer-credentials"
    >
      <div className="text-[10px] uppercase tracking-[0.2em] text-navy/70 font-extrabold mb-1">
        Legal Credentials
      </div>
      <div><span className="text-navy/50">GSTIN:</span>{" "}
        <span className="font-mono font-bold text-navy/80">06DUWPK7336G1Z2</span>
      </div>
      <div className="mt-0.5">
        <span className="text-navy/50">Udyam Reg:</span>{" "}
        <span className="font-mono font-bold text-navy/80">UDYAM-HR-03-0161073</span>
      </div>
      <div className="mt-0.5">
        <span className="text-navy/50">Registered Address:</span>{" "}
        T12-601, RPS Savana, Sector-88, Faridabad, Haryana, 121002
      </div>
    </div>
  </div>
);

const ScopeNote = () => (
  <div className="rounded-xl bg-sand-alt border-2 border-border px-3.5 py-3 not-prose">
    <div className="text-[11px] uppercase tracking-wider text-navy/70 font-bold">Scope</div>
    <p className="mt-1 text-[13.5px] text-foreground leading-relaxed">
      <strong>Mera Naksha provides conceptual planning and design assistance
      services.</strong> All delivered drawings are <strong>Phase-1 Concept Plans</strong>{" "}
      meant to be reviewed and adapted by a licensed local architect or
      structural engineer before construction or municipal submission.
    </p>
  </div>
);

const today = "27 Feb 2026";

// ---------------------- Privacy ----------------------

export const Privacy = () => {
  const location = useLocation();
  return (
    <LegalShell title="Privacy Policy" Icon={ShieldCheck} kicker="Privacy" lastUpdated={today} testId="privacy-page">
      <ScopeNote />
      <p>
        This Privacy Policy explains how Mera Naksha ("we", "us", "Team Mera Naksha")
        collects, uses, and protects the personal information of customers and
        professionals who use{" "}
        <code>meranaksha.com</code> and related services.
      </p>

      <h2>1. Information we collect</h2>
      <ul>
        <li><strong>Account data:</strong> name, mobile number, email, city — provided during login / registration / inquiries.</li>
        <li><strong>Order data:</strong> purchased designs, payment status, screenshots uploaded for verification, optional UPI reference.</li>
        <li><strong>Communications:</strong> any messages you send via WhatsApp, email, or the inquiry form.</li>
        <li><strong>Device data:</strong> IP address, browser type, OS — used for security (brute-force / spam detection) only.</li>
      </ul>

      <h2>2. How we use your information</h2>
      <ul>
        <li>To verify your payment and unlock the purchased concept plan.</li>
        <li>To assign your custom-design inquiry to the right verified professional.</li>
        <li>To send transactional WhatsApp / email updates about your order.</li>
        <li>To improve the platform, prevent fraud, and comply with Indian law.</li>
      </ul>

      <h2>3. Sharing</h2>
      <p>
        We <strong>never sell</strong> your data. Limited sharing happens only with:
      </p>
      <ul>
        <li>The assigned verified professional (only when you submit a consultation inquiry).</li>
        <li>Payment processors (e.g. Razorpay) when you use a card / UPI checkout.</li>
        <li>Authorities, when required by Indian law via a valid legal request.</li>
      </ul>

      <h2>4. Data retention</h2>
      <p>
        Order and account data is retained while your account is active and for up to 7 years
        after, to comply with financial-record-keeping obligations. You can request earlier
        deletion by emailing <strong>support@meranaksha.com</strong>.
      </p>

      <h2>5. Security</h2>
      <p>
        We use HTTPS, hashed passwords (bcrypt), httpOnly session cookies, and rate-limiting
        on authentication endpoints. No method of transmission is 100% secure, so please
        notify us immediately of any suspected account compromise.
      </p>

      <h2>6. Your rights</h2>
      <p>
        Under the Digital Personal Data Protection Act, 2023 you may request access,
        correction, or deletion of your personal data. Contact us at{" "}
        <strong>support@meranaksha.com</strong> and we will respond within 30 days.
      </p>

      <h2>7. Cookies</h2>
      <p>
        We use only first-party cookies essential to authentication (admin / customer /
        professional sessions). No third-party advertising or tracking cookies are set.
      </p>

      <h2>8. Updates</h2>
      <p>
        We may update this policy. Material changes will be announced on this page with
        a refreshed "Last updated" date.
      </p>
    </LegalShell>
  );
};

// ---------------------- Refund ----------------------

export const Refund = () => (
  <LegalShell title="Refund Policy" Icon={RefreshCcw} kicker="Refund Policy" lastUpdated={today} testId="refund-page">
    <ScopeNote />
    <p>
      Because Concept Plans are <strong>digital goods</strong> delivered the
      moment your payment is verified, refunds are limited as detailed below.
    </p>

    <h2>1. When refunds apply</h2>
    <ul>
      <li>
        <strong>Duplicate payment:</strong> if you paid more than once for the same Order ID,
        we refund the duplicate amount within 7 working days.
      </li>
      <li>
        <strong>Payment failed / not unlocked:</strong> if your payment was debited but the
        download did not unlock within 24 hours of admin verification, you can request a
        refund OR ask us to re-issue the design.
      </li>
      <li>
        <strong>Wrong design delivered:</strong> if we shared a design that does not match the
        plot size / facing / BHK you purchased, full refund or replacement at your choice.
      </li>
    </ul>

    <h2>2. When refunds do not apply</h2>
    <ul>
      <li>You have already downloaded the unlocked PDF.</li>
      <li>You changed your mind after the design was unlocked.</li>
      <li>Construction-stage issues (which require a licensed architect on site).</li>
    </ul>

    <h2>3. How to request a refund</h2>
    <p>
      WhatsApp <strong>+91 97176 67115</strong> with your Order ID and a short reason. Most
      decisions are communicated within 24 hours; verified refunds reach your account in
      5–7 working days.
    </p>

    <h2>4. Chargebacks</h2>
    <p>
      If you raise a chargeback through your bank without first contacting us, your
      account may be suspended pending bank investigation. Please write to us first —
      we will almost always resolve it faster than the bank can.
    </p>
  </LegalShell>
);

// ---------------------- Terms ----------------------

export const Terms = () => (
  <LegalShell title="Terms &amp; Conditions" Icon={ScrollText} kicker="Terms" lastUpdated={today} testId="terms-page">
    <ScopeNote />
    <h2>1. About Mera Naksha</h2>
    <p>
      Mera Naksha is a managed marketplace operated by Team Mera Naksha that provides
      <strong> conceptual planning, design assistance, and inquiry routing</strong>{" "}
      between Indian homeowners and verified design professionals. We do not perform
      construction, statutory approvals, or on-site supervision.
    </p>

    <h2>2. Concept plans only</h2>
    <p>
      Every drawing delivered through the Mera Naksha Design Library is a{" "}
      <strong>Phase-1 Concept Plan</strong>. Before construction or municipal submission you
      must engage a licensed local architect or structural engineer to verify and adapt
      the drawing to your plot's bye-laws, soil conditions, and statutory rules.
    </p>

    <h2>3. Accounts</h2>
    <ul>
      <li>You confirm you are 18 years or older and a resident of India.</li>
      <li>Mobile numbers and emails you provide must belong to you.</li>
      <li>You are responsible for all activity on your account; never share your OTP or admin password.</li>
    </ul>

    <h2>4. Payments</h2>
    <p>
      Listed prices are in Indian Rupees and inclusive of applicable GST. We accept UPI,
      bank transfer, and (where activated) card payments via Razorpay. Manual payments
      (UPI / bank transfer) require uploading a screenshot or reference number; admins
      verify within 24 hours and the design is unlocked automatically thereafter.
    </p>

    <h2>5. Intellectual property</h2>
    <p>
      Concept plans, logos, brand elements, and source code are owned by Team Mera Naksha
      and licensed to you for personal, non-commercial use after purchase. You may not
      resell, re-licence, or distribute the drawings without written consent.
    </p>

    <h2>6. Acceptable use</h2>
    <p>
      You agree not to (a) misuse OTPs or attempt to brute-force authentication, (b) upload
      unlawful or infringing content via inquiries or portfolio submissions, or (c) impersonate
      another professional or customer.
    </p>

    <h2>7. Limitation of liability</h2>
    <p>
      To the maximum extent permitted by Indian law, Mera Naksha's aggregate liability for
      any claim arising out of or related to these Terms is limited to the amount you paid
      for the specific concept plan or service in question.
    </p>

    <h2>8. Governing law &amp; jurisdiction</h2>
    <p>
      These Terms are governed by the laws of India. Courts at <strong>Bihar (Sasaram /
      Patna)</strong> have exclusive jurisdiction over any dispute.
    </p>

    <h2>9. Changes</h2>
    <p>
      We may update these Terms by posting a revised version here with a new
      "Last updated" date. Continued use after the update constitutes acceptance.
    </p>
  </LegalShell>
);

// ---------------------- Contact ----------------------

export const Contact = () => (
  <LegalShell title="Contact Us" Icon={Phone} kicker="Contact" lastUpdated={today} testId="contact-page">
    <ScopeNote />
    <p>
      We are a small team — most queries are resolved within an hour over WhatsApp.
    </p>

    <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
      <a
        href="https://wa.me/919717667115"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="contact-cta-whatsapp"
        className="rounded-2xl bg-emerald-600 text-white p-5 shadow-sm hover:bg-emerald-700"
      >
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold text-white/85">
          <FaWhatsapp className="h-4 w-4" /> WhatsApp (fastest)
        </div>
        <div className="mt-1 text-xl font-extrabold tracking-tight">+91 97176 67115</div>
        <div className="text-[12.5px] text-white/85 mt-0.5">Mon–Sat · 9:30am – 8:30pm IST</div>
      </a>
      <a
        href="mailto:support@meranaksha.com"
        data-testid="contact-cta-email"
        className="rounded-2xl bg-white border-2 border-border p-5 shadow-sm hover:border-navy"
      >
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold text-navy/70">
          <Mail className="h-4 w-4" /> Email
        </div>
        <div className="mt-1 text-xl font-extrabold tracking-tight text-navy">support@meranaksha.com</div>
        <div className="text-[12.5px] text-muted-foreground mt-0.5">For policy, partnership and press queries</div>
      </a>
    </div>

    <h2>Address</h2>
    <p>
      <FileText className="inline h-4 w-4 mr-1 text-navy" /> Team Mera Naksha,
      Sasaram, Bihar 821115, India.
    </p>

    <h2>Want to join as a professional?</h2>
    <p>
      Architects, structural engineers, MEP engineers, and interior designers can apply
      via the <Link to="/professional/register" className="text-navy font-bold">Hire Professionals registration page</Link>.
      Verification by our team takes up to 48 hours.
    </p>

    <h2>Press / Partnerships</h2>
    <p>
      Drop us a note at <strong>support@meranaksha.com</strong> with a one-line context line
      in the subject. We try to respond within two working days.
    </p>
  </LegalShell>
);
